import {
  useState,
  createContext,
  useContext,
  ReactNode,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import { ScoutLocation } from '@/types/location';
import { Views } from '@/types/view';

interface IContext {
  locations: ScoutLocation[];
  setLocations: Dispatch<SetStateAction<ScoutLocation[]>>;
  activeSequenceIndex: number;
  setActiveSequenceIndex: Dispatch<SetStateAction<number>>;
  activeFrameIndex: {
    value: number;
  };
  setActiveFrameIndex: Dispatch<
    SetStateAction<{
      value: number;
    }>
  >;
  selectedLocationIds: string[];
  setSelectedLocationIds: Dispatch<SetStateAction<string[]>>;
  lastSelectedView: Views;
  setLastSelectedView: Dispatch<SetStateAction<Views>>;
  forceMapUpdate: boolean;
  setForceMapUpdate: Dispatch<SetStateAction<boolean>>;
  quarantined: string[];
  setQuarantined: Dispatch<SetStateAction<string[]>>;
}

interface Props {
  children: ReactNode;
  locations: ScoutLocation[];
}

const ScoutContext = createContext<IContext | null>(null);

const ScoutProvider: React.FC<Props> = ({
  children,
  locations: initialLocations,
}) => {
  const [locations, setLocations] = useState<ScoutLocation[]>(initialLocations);
  const [activeSequenceIndex, setActiveSequenceIndex] = useState(0);
  const [activeFrameIndex, setActiveFrameIndex] = useState({ value: 0 });
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [lastSelectedView, setLastSelectedView] = useState<Views>(
    Views.Location,
  );
  const [quarantined, setQuarantined] = useState<string[]>([]);
  const [forceMapUpdate, setForceMapUpdate] = useState(false);

  useEffect(() => {
    setSelectedLocationIds((prevState: string[]) => {
      const remainingIds = prevState.filter(id =>
        locations.some(location => location._id === id),
      );

      return remainingIds;
    });
  }, [locations, setSelectedLocationIds]);

  useEffect(() => {
    if (forceMapUpdate) {
      setForceMapUpdate(false);
    }
  }, [forceMapUpdate]);

  return (
    <ScoutContext.Provider
      value={{
        locations,
        setLocations,
        activeSequenceIndex,
        setActiveSequenceIndex,
        activeFrameIndex,
        setActiveFrameIndex,
        selectedLocationIds,
        setSelectedLocationIds,
        lastSelectedView,
        setLastSelectedView,
        forceMapUpdate,
        setForceMapUpdate,
        quarantined,
        setQuarantined,
      }}>
      {children}
    </ScoutContext.Provider>
  );
};

const useScout = () => {
  const context = useContext(ScoutContext);
  if (!context) {
    throw new Error('useScout must be used within the ScoutProvider');
  }

  return context;
};

export { ScoutProvider, useScout };
